import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { AlertTriangle, Bike } from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function OpeningHours() {
  const [hours, setHours] = useState(null);
  const [paused, setPaused] = useState(false);
  const [deliveryPaused, setDeliveryPaused] = useState(false);

  const load = async () => {
    const [h, s] = await Promise.all([api.get("/admin/opening-hours"), api.get("/admin/settings")]);
    setHours(h.data); setPaused(!!s.data.orders_paused); setDeliveryPaused(!!s.data.delivery_paused);
  };
  useEffect(() => { load(); }, []);

  const setDay = (day, k, v) => setHours((p) => ({ ...p, [day]: { ...p[day], [k]: v } }));

  const save = async () => {
    await api.put("/admin/opening-hours", hours);
    toast.success("Opening hours saved");
  };
  const togglePause = async (v) => {
    setPaused(v);
    await api.put("/admin/settings", { orders_paused: v });
    toast.success(v ? "Online ordering paused" : "Online ordering resumed");
  };
  const toggleDeliveryPause = async (v) => {
    setDeliveryPaused(v);
    await api.put("/admin/settings", { delivery_paused: v });
    toast.success(v ? "Delivery paused — collection only" : "Delivery orders resumed");
  };

  if (!hours) return null;

  return (
    <div className="px-4 md:px-6 py-4 max-w-2xl">
      <h1 className="font-head text-2xl md:text-3xl font-bold text-brand-dark mb-5">Opening Hours</h1>

      <div className={`rounded-2xl p-4 mb-6 border flex items-center justify-between ${paused ? "bg-red-50 border-red-200" : "bg-white border-brand-dark/5"}`}>
        <div className="flex items-center gap-3">
          <AlertTriangle className={`w-5 h-5 ${paused ? "text-red-500" : "text-brand-dark/40"}`} />
          <div>
            <p className="font-semibold text-brand-dark">Pause online orders</p>
            <p className="text-sm text-brand-dark/50">Instantly stop accepting new orders.</p>
          </div>
        </div>
        <Switch data-testid="pause-toggle" checked={paused} onCheckedChange={togglePause} />
      </div>

      <div className={`rounded-2xl p-4 mb-6 border flex items-center justify-between ${deliveryPaused ? "bg-amber-50 border-amber-200" : "bg-white border-brand-dark/5"}`}>
        <div className="flex items-center gap-3">
          <Bike className={`w-5 h-5 ${deliveryPaused ? "text-amber-600" : "text-brand-dark/40"}`} />
          <div>
            <p className="font-semibold text-brand-dark">Pause delivery orders</p>
            <p className="text-sm text-brand-dark/50">Stop taking delivery orders but keep collection open.</p>
          </div>
        </div>
        <Switch data-testid="delivery-pause-toggle" checked={deliveryPaused} onCheckedChange={toggleDeliveryPause} />
      </div>

      <div className="bg-white rounded-2xl border border-brand-dark/5 divide-y">
        {DAYS.map((day) => (
          <div key={day} className="flex items-center gap-3 p-3" data-testid={`hours-${day}`}>
            <span className="w-24 font-semibold text-brand-dark capitalize">{day}</span>
            <label className="flex items-center gap-2 text-sm text-brand-dark/60">
              <Switch data-testid={`closed-${day}`} checked={!hours[day].closed} onCheckedChange={(v) => setDay(day, "closed", !v)} />
              {hours[day].closed ? "Closed" : "Open"}
            </label>
            {!hours[day].closed && (
              <div className="flex items-center gap-2 ml-auto">
                <Input type="time" value={hours[day].open} onChange={(e) => setDay(day, "open", e.target.value)} className="rounded-xl h-9 w-28" />
                <span className="text-brand-dark/40">–</span>
                <Input type="time" value={hours[day].close} onChange={(e) => setDay(day, "close", e.target.value)} className="rounded-xl h-9 w-28" />
              </div>
            )}
          </div>
        ))}
      </div>

      <Button data-testid="save-hours-btn" onClick={save} className="mt-5 h-12 rounded-xl bg-brand-teal hover:bg-brand-teal-dark text-white font-bold px-8">Save hours</Button>
    </div>
  );
}
